import { ThemePerformanceSortHorizon } from '@api';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useGetThemePerformance } from '@terminal/react-query-hooks/themes/useGetThemePerformance';
import { queryKeys } from '@terminal/routes/query-params';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChartTitle.module.scss';

type PickedThemePerformanceSortHorizon = Extract<
  ThemePerformanceSortHorizon,
  'intraday' | '1w' | '1m' | '3m' | '6m' | '1y'
>;

const isValidHorizon = (
  horizon: string | null
): horizon is PickedThemePerformanceSortHorizon => {
  return (
    typeof horizon === 'string' &&
    ['intraday', '1w', '1m', '3m', '6m', '1y'].includes(horizon)
  );
};

interface ChartTitleProps {
  description: ReactNode;
}

export const ChartTitle = ({ description }: ChartTitleProps) => {
  const { t } = useTranslation(['widget']);

  const { params } = useQueryParams();
  const activeHorizon = params[queryKeys.themeHorizon];
  const themeId = params[queryKeys.themeId];

  const horizons: Record<PickedThemePerformanceSortHorizon, string> = {
    intraday: t('widget:dashboardThemePerf.chartTitle.horizon_1d'),
    '1w': t('widget:dashboardThemePerf.chartTitle.horizon_1w'),
    '1m': t('widget:dashboardThemePerf.chartTitle.horizon_1m'),
    '3m': t('widget:dashboardThemePerf.chartTitle.horizon_3m'),
    '6m': t('widget:dashboardThemePerf.chartTitle.horizon_6m'),
    '1y': t('widget:dashboardThemePerf.chartTitle.horizon_1y'),
  };

  const { themes = [] } = useGetThemePerformance(
    { ids: themeId ?? '' },
    !!themeId
  );

  if (!isValidHorizon(activeHorizon)) {
    return null;
  }

  const performanceData = themes[0]?.performance;
  const headlineHorizon = themes[0]?.intraday_metadata.headline_horizon;
  const rawHorizonReturn =
    activeHorizon === 'intraday'
      ? performanceData?.[headlineHorizon ?? '1d']
      : (performanceData?.[activeHorizon] ?? undefined);
  const horizonReturn = formatNumber({
    isPercentage: true,
    value: rawHorizonReturn,
  });

  return (
    <div className={styles.root}>
      <p className={styles.description}>{description}</p>
      <div
        className={clsx(styles.pill, {
          [styles.negative]: !!rawHorizonReturn && rawHorizonReturn < 0,
        })}
      >
        <span className={styles.horizonLabel}>{horizons[activeHorizon]}</span>
        <span
          className={clsx(styles.returnValue, {
            [styles.negative]: !!rawHorizonReturn && rawHorizonReturn < 0,
          })}
        >
          {horizonReturn}
        </span>
      </div>
    </div>
  );
};
